"use client";

import { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
gsap.registerPlugin(useGSAP);
export default function Studio() {
  const container = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const mm = gsap.matchMedia();
    mm.add({
      reduced: "(prefers-reduced-motion: reduce)",
      normal: "(prefers-reduced-motion: no-preference)"
    }, context => {
      const conditions = context.conditions as {
        reduced: boolean;
        normal: boolean;
      };
      if (conditions.reduced) {
        gsap.set([".studio-portrait", ".studio-caption", ".studio-kicker", ".studio-heading-line", ".studio-paragraph", ".studio-cta"], {
          opacity: 1,
          y: 0,
          clipPath: "inset(0% 0% 0% 0%)"
        });
        return;
      }
      const tl = gsap.timeline({
        defaults: {
          ease: "power3.out"
        }
      });
      tl.from(".studio-portrait", {
        y: 32,
        opacity: 0,
        duration: 0.9,
        ease: "cubic-bezier(0.22,1,0.36,1)",
        stagger: 0.15
      }).from(".studio-caption", {
        opacity: 0,
        y: 12,
        duration: 0.6
      }, "-=0.4").from(".studio-kicker", {
        opacity: 0,
        y: 12,
        duration: 0.6
      }, "-=0.6").from(".studio-hairline", {
        scaleX: 0,
        transformOrigin: "left center",
        duration: 0.6
      }, "-=0.4").from(".studio-heading-line", {
        yPercent: 110,
        opacity: 0,
        duration: 1.1,
        ease: "cubic-bezier(0.22,1,0.36,1)",
        stagger: 0.09
      }, "-=0.3").from(".studio-paragraph", {
        opacity: 0,
        y: 16,
        duration: 0.7,
        stagger: 0.12
      }, "-=0.6").from(".studio-cta", {
        opacity: 0,
        y: 12,
        duration: 0.6
      }, "-=0.4");
    });
  }, {
    scope: container
  });
  return <section data-section-id="23084" id="why-you-work-directly-with-me" data-nav="light" className="bg-background text-foreground">
      <div ref={container} className="mx-auto w-full max-w-[1360px] px-5 sm:px-8 lg:px-14 py-[clamp(96px,18vh,220px)]">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="studio-portrait overflow-hidden rounded-2xl aspect-[4/5] w-full">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/ChatGPT-Image-Sep-27-2026-10_24_35-PM-1.png" alt="Professional portrait of Yassine Essaadi, independent SEO consultant" loading="lazy" decoding="async" className="h-full w-full object-cover" />
            </div>
            <p className="studio-caption mt-4 font-sans text-[0.9375rem] tracking-wide" style={{
            color: "var(--muted-foreground)"
          }}>
              Yassine Essaadi, Independent SEO Consultant
            </p>
          </div>

          <div className="lg:col-span-7 lg:pl-4">
            <div className="flex items-center gap-4">
              <span className="studio-kicker font-sans text-[0.875rem] font-medium uppercase" style={{
              letterSpacing: "0.18em",
              color: "var(--muted-foreground)"
            }}>
                My Approach
              </span>
              <span className="studio-hairline block h-px w-10" style={{
              backgroundColor: "var(--border)"
            }} aria-hidden="true" />
            </div>

            <h2 className="mt-8 font-serif font-normal text-foreground" style={{
            fontSize: "clamp(2.25rem, 4.5vw, 3.75rem)",
            letterSpacing: "-0.03em",
            lineHeight: 0.96
          }}>
              <span className="block overflow-hidden">
                <span className="studio-heading-line block">Work Directly With</span>
              </span>
              <span className="block overflow-hidden">
                <span className="studio-heading-line block">
                  Your <em className="italic">SEO Consultant</em>
                </span>
              </span>
            </h2>

            <div className="mt-12 max-w-[560px] space-y-6">
              <p className="studio-paragraph font-sans text-foreground" style={{
              fontSize: "clamp(1.1875rem, 1.3vw, 1.4rem)",
              lineHeight: 1.55
            }}>
                When you hire me as your <Link to="/FreelanceSeoConsultantPage" className="text-primary underline underline-offset-4 hover:text-primary/80 transition-colors font-medium">freelance SEO consultant</Link>, you get direct communication and senior-level attention from day one. You communicate directly with me throughout the engagement, providing clear communication, senior-level involvement and consistent strategic oversight.
              </p>
              <p className="studio-paragraph font-sans text-foreground" style={{
              fontSize: "clamp(1.1875rem, 1.3vw, 1.4rem)",
              lineHeight: 1.55
            }}>
                Working with an independent consultant provides greater strategic continuity. Instead of recycled agency playbooks, you receive a tailored strategy designed around your specific market, technical architecture, and revenue targets.
              </p>
              <p className="studio-paragraph font-sans text-foreground" style={{
              fontSize: "clamp(1.1875rem, 1.3vw, 1.4rem)",
              lineHeight: 1.55
            }}>
                You also benefit from direct accountability and transparent reporting. Every milestone, deliverable, and ranking shift is communicated clearly with honest measurement and practical recommendations that drive sustainable growth.
              </p>
            </div>

            <div className="studio-cta mt-12">
              <Button asChild variant="outline" className="h-auto rounded-lg border-primary bg-transparent px-7 py-3.5 font-sans text-[1.0625rem] font-medium text-primary transition-colors duration-300 hover:bg-primary hover:text-white">
                <Link to="/AboutMePage">Learn about my process.</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}